import { Banknote, CreditCard, QrCode, Nfc } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PaymentGateway } from '@/types/sale';
import type { PaymentMethod } from '@/types/payment';

interface PaymentMethodChooserProps {
  methods: PaymentMethod[];
  selectedId: string | null;
  onSelect: (method: PaymentMethod) => void;
}

const GATEWAY_META: Record<PaymentGateway, { icon: LucideIcon; label: string }> = {
  cash: { icon: Banknote, label: 'Tunai' },
  'e-money': { icon: CreditCard, label: 'E-Money' },
  midtrans: { icon: QrCode, label: 'QRIS' },
  // Distinct icon from e-money: both are cards to a cashier, and picking the
  // wrong one sends the customer to the wrong physical device.
  'edc-permata': { icon: Nfc, label: 'Kartu (EDC)' },
};

/**
 * A gateway can offer more than one tender, so the gateway alone cannot name the
 * button. Overrides are keyed by `paymentMethod`.
 *
 * One EDC machine takes both a card and a QR scan; without the first entry both
 * rendered as "Kartu (EDC)" with the same contactless icon, and the only thing
 * telling them apart was the small print — on the screen where a cashier decides
 * whether to ask for a card or a phone.
 *
 * Midtrans is now the same story: plain QRIS and QRIS (ASPI) are the same charge
 * shown in different frames, and without the second entry BOTH rendered as a
 * button reading exactly "QRIS".
 */
const METHOD_META: Record<string, { icon: LucideIcon; label: string }> = {
  'edc-qris': { icon: QrCode, label: 'QRIS (EDC)' },
  'qris-aspi': { icon: QrCode, label: 'QRIS (ASPI)' },
  // Midtrans's own page, limited to QRIS at the till — still a QR the customer
  // scans, but shown by Snap rather than drawn by us.
  snap: { icon: QrCode, label: 'QRIS (Snap)' },
};

export function PaymentMethodChooser({
  methods,
  selectedId,
  onSelect,
}: PaymentMethodChooserProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {methods.map((m) => {
        const meta =
          (m.paymentMethod ? METHOD_META[m.paymentMethod] : undefined) ??
          GATEWAY_META[m.paymentGateway] ?? { icon: CreditCard, label: m.name };
        const Icon = meta.icon;
        const active = m.id === selectedId;
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => onSelect(m)}
            className={cn(
              'flex flex-col items-center gap-2 rounded-lg border-2 px-4 py-5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'border-primary bg-primary/5 shadow-md'
                : 'border-border bg-card hover:-translate-y-0.5 hover:border-primary/40',
            )}
          >
            <span
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-full',
                active ? 'bg-primary text-primary-foreground' : 'bg-secondary text-ink-600',
              )}
            >
              <Icon className="h-6 w-6" />
            </span>
            <span className="font-display font-semibold text-ink-900">{meta.label}</span>
            <span className="text-xs text-ink-400">{m.name}</span>
          </button>
        );
      })}
    </div>
  );
}
